d<template>
  <div>
    <div v-if="!isBusy && items.length === 0">
      <b-alert variant="info" show>
        <strong>Hinweis:</strong> Es wurde noch keine Resteliste für diese Fahrt angelegt. Du kannst jetzt mit dem
        Erstellen einer Resteliste beginnen. Wichtig: Du musst für alle Zeilen auf den Speichern-Button drücken,
        sonst werden die eingegebenen Mengen nicht gespeichert!
      </b-alert>
      <b-btn @click="loadDefaultList()" variant="primary" size="lg">Resteliste erstellen</b-btn>
    </div>
    <div v-else>
      <b-alert v-if="!isBusy" variant="info" show>
        <strong>Wichtig:</strong> Die hier angegebenen Reste müssen dem Folgekurs auch auf jeden Fall zur Verfügung gestellt werden!
      </b-alert>
      <div class="clearfix">
        <b-btn size="sm" variant="outline-primary" @click="fetchData()" :disabled="isBusy" class="float-right mb-2">
          <i class="fas fa-sync-alt"></i> Aktualisieren
        </b-btn>
      </div>

      <b-table
          striped
          hover
          :busy.sync="isBusy"
          :items="filteredItems"
          :fields="fields"
          :sort-compare="sortCompare"
          stacked="sm"
          small
          bordered
          head-variant="dark"
      >
        <template slot="top-row">
          <td class="d-none d-md-table-cell">
            <b-form-select size="sm" v-model="filters.ingredient_category" :options="filterOptions.ingredient_categories">
              <template #first>
                <b-form-select-option value="">---</b-form-select-option>
              </template>
            </b-form-select>
          </td>
          <td id="ingredient-filter">
            <b-form-input size="sm" v-model="filters.ingredient_title" placeholder="Zutat suchen..." debounce="200"></b-form-input>
          </td>
          <td class="d-none d-md-table-cell"><b-form-input size="sm" v-model="filters.amount_available" debounce="200" class="amount-input"></b-form-input></td>
          <td class="d-none d-md-table-cell"><b-form-input size="sm" v-model="filters.base_unit_title" debounce="200" class="base-unit-input"></b-form-input></td>
          <td class="d-none d-md-table-cell">&nbsp;</td>
        </template>
        <template v-slot:cell(ingredient_title)="{ item }">
          <RowButtonComponent :items="items" :item="item" :api-url="apiUrl" :can-edit="canEdit" class="d-md-none float-right" />
          <span>{{ item.ingredient_title }}</span>
        </template>
        <template v-slot:cell(amount_available)="{ item }">
          <div v-if="!item._editing" class="float-right">
            <span>{{ formatAmountNeeded(item.amount_available, null, item) }}</span>
            <span class="d-md-none">{{ item.base_unit_title }}</span>
          </div>
          <b-input-group v-if="item._editing" class="d-md-none">
            <template #append>
              <b-input-group-text class="d-md-none">{{ item.base_unit_title }}</b-input-group-text>
            </template>
            <b-form-input size="sm" v-model="item._new_amount_available"
                          type="number" class="amount-input"
                          @keydown.enter="$refs['buttons'].finishEditItem(item)"
                          @keydown.esc="$refs['buttons'].cancelEditItem(item)"
                          :step="Math.pow(10, item.base_unit_precision * -1)"></b-form-input>
          </b-input-group>
          <b-form-input v-if="item._editing" size="sm" v-model="item._new_amount_available"
                        type="number" class="d-none d-md-block amount-input"
                        @keydown.enter="$refs['buttons'].finishEditItem(item)"
                        @keydown.esc="$refs['buttons'].cancelEditItem(item)"
                        :step="Math.pow(10, item.base_unit_precision * -1)"></b-form-input>
        </template>
        <template v-slot:cell(actions)="{ item }">
          <RowButtonComponent ref="buttons" :items="items" :item="item" :api-url="apiUrl" :can-edit="canEdit" />
        </template>
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Lade Resteliste...</strong>
          </div>
        </template>
        <template slot="bottom-row" v-if="canEdit">
          <td class="d-none d-md-table-cell"><strong>Eintrag hinzufügen:</strong></td>
          <td>
            <v-multiselect v-model="newItem.ingredient" :options="filteredIngredients"
                           track-by="value" label="text" :show-labels="false" :searchable="true" :disabled="isSaving">
            </v-multiselect>
          </td>
          <td>
            <b-input-group class="d-md-none">
              <template #append>
                <b-input-group-text>{{ newItem.ingredient ? newItem.ingredient.base_unit_title : '' }}</b-input-group-text>
                <b-btn class="d-sm-none" @click="createItem" size="xs" variant="primary" :disabled="isSaving">
                  <i v-if="!isSaving" class="fas fa-save" />
                  <b-spinner v-if="isSaving" small variant="light"></b-spinner>
                  Hinzufügen
                </b-btn>
              </template>
              <b-form-input type="number" size="sm" v-model="newItem.amount" placeholder="Menge"
                            @keydown.enter.native="createItem" class="amount-input"
                            :step="Math.pow(10, (newItem.ingredient ? newItem.ingredient.base_unit_precision : 1) * -1)"></b-form-input>
            </b-input-group>
            <b-form-input type="number" size="sm" v-model="newItem.amount" placeholder="Menge"
                          @keydown.enter.native="createItem" class="amount-input d-none d-md-block"
                          :step="Math.pow(10, (newItem.ingredient ? newItem.ingredient.base_unit_precision : 1) * -1)"></b-form-input>
          </td>
          <td class="d-none d-md-table-cell">{{ newItem.ingredient ? newItem.ingredient.base_unit_title : '' }}</td>
          <td class="d-none d-md-table-cell">
            <b-btn class="save-row-toggle" @click="createItem" size="xs" variant="primary" :disabled="isSaving">
              <i v-if="!isSaving" class="fas fa-save" />
              <b-spinner v-if="isSaving" small variant="light"></b-spinner>
            </b-btn>
          </td>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import HelperMixin from "../trip-shopping-list/HelperMixin"
import RowButtonComponent from "./RowButtonComponent";
import Pusher from "pusher-js"
const axios = require('axios')

export default {
  mixins: [ HelperMixin ],
  components: {
    RowButtonComponent,
  },
  data() {
    return {
      apiUrl: this.$parent.apiUrl,
      canEdit: this.$parent.canEdit,
      pusher: this.$parent.pusher,
      filterOptions: {
        'ingredients': [],
        'ingredient_categories': [],
      },
      filters: {
        'ingredient_category': '',
        'ingredient_title': '',
        'amount_needed': '',
        'base_unit_title': '',
      },
      fields: [
        { key: 'ingredient_category', label: 'Kategorie', sortable: true, sortByFormatted: true, formatter: 'formatIngredientCategory', tdClass: 'd-none d-md-table-cell' },
        { key: 'ingredient_title', label: 'Zutat', sortable: true },
        { key: 'amount_available', label: 'Menge', thClass: 'text-right' },
        { key: 'base_unit_title', label: 'Einheit', tdClass: 'd-none d-md-table-cell' },
        { key: 'actions', label: '', tdClass: 'd-none d-md-table-cell' },
      ],
      newItem: {
        ingredient: null,
        amount: '',
      },
      items: [],
      defaultItems: [],
      isBusy: false,
      isSaving: false,
    }
  },
  computed: {
    filteredItems() {
      return this.items.filter(item => {
        return Object.keys(this.filters).every(key => {
          if (this.filters[key] === '') {
            return true;
          }
          if (Number.isInteger(item[key])) {
            return item[key] === this.filters[key];
          }
          return String(item[key]).toLowerCase().includes(this.filters[key].toLowerCase());
        })
      })
    },
    filteredIngredients() {
      return this.filterOptions.ingredients.filter(item => {
        return !this.items.find(obj => {
          return obj.is_private && obj.ingredient_id === item.value
        })
      })
    },
  },
  mounted() {
    this.fetchData()
    let pusher = new Pusher(this.pusher.key, { cluster: 'eu' })
    let channel = pusher.subscribe(this.pusher.channel)

    channel.bind('remains-list-item-created', (data) => {
      console.log('[Pusher] received remains-list-item-created event', data)
      let item = this.items.find(e => {
        return e.id === data.id || e.ingredient_id === data.ingredient_id
      })
      if (item) {
        item.id = data.id
        item.amount_available = data.amount_available
      } else {
        this.items.push(data)
      }
    })

    channel.bind('remains-list-item-updated', (data) => {
      console.log('[Pusher] received remains-list-item-updated event', data)
      let item = this.items.find(e => {
        return e.id === data.id
      })
      if (item) {
        item._editing = false
        item.amount_available = data.amount_available
      }
    })

    channel.bind('remains-list-item-deleted', (data) => {
      console.log('[Pusher] received remains-list-item-deleted event', data)
      let item = this.items.find(e => {
        return e.id === data.id
      })
      if (item) {
        const index = this.items.indexOf(item)
        // item may have already been deleted locally
        if (index >= 0) {
          this.items.splice(index, 1)
        }
      }
    })
  },
  methods: {
    fetchData() {
      this.isBusy = true
      let promise = axios.get(this.apiUrl);

      return promise.then((result) => {
        this.items = result.data.remainsList
        this.defaultItems = result.data.defaultRemainsList
        this.filterOptions.ingredients = result.data.ingredients
        this.filterOptions.ingredient_categories = result.data.ingredientCategories
        this.isBusy = false
      }).catch(error => {
        this.isBusy = false
      })
    },
    loadDefaultList() {
      if (this.defaultItems.length) {
        this.defaultItems.map(obj => {
          this.$set(obj, 'id', null)
          this.$set(obj, '_editing', true)
          this.$set(obj, '_deleting', false)
          this.$set(obj, '_saving', false)
          this.$set(obj, '_new_amount_available', obj.amount_available)
          this.items.push(obj)
        })
      } else {
        this.fetchData().then(() => {
          this.loadDefaultList()
        })
      }
    },
    createItem() {
      if (!this.newItem.ingredient || !this.newItem.amount) {
        return
      }

      this.isSaving = true
      axios.post(this.apiUrl, {
        ingredient_id: this.newItem.ingredient.value,
        amount_available: this.newItem.amount,
      }).then((result) => {
        this.$set(this, 'isSaving', false)
        this.$set(this, 'newItem', {ingredient: null, amount: '', location: ''})

        let item = this.items.find(obj => {
          return typeof obj !== "undefined" && obj.id === result.data.id
        })
        // only add item if it has not yet been added by the Pusher call
        if (item) {
          item.amount_available = result.data.amount_available
        } else {
          this.items.push(result.data)
        }
      }).catch(error => {
        this.$set(this, 'isSaving', false)
      })
    },
    sortCompare(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale) {
      let aValue = formatter ? formatter(aRow[key]) : aRow[key]
      let bValue = formatter ? formatter(bRow[key]) : bRow[key]
      if (key === 'ingredient_category') {
        aValue += '|' + aRow['ingredient_title']
        bValue += '|' + bRow['ingredient_title']
      }
      return aValue.localeCompare(bValue, compareLocale, compareOptions)
    }
  }
}
</script>

<style scoped>
  .save-row-toggle {
    width: 2rem;
  }
  .amount-input {
    margin-left: auto;
    max-width: 8em;
  }
  .base-unit-input {
    max-width: 8em;
  }
</style>